/**
 * CetusEd Community Portal — Auth Hook
 *
 * COMPLIANCE: Centralizes access to the auth context. Every component
 *   that reads auth state MUST use this hook to ensure null-checking
 *   per the fail-closed rule (⛔ Rule 2).
 *
 * MIGRATION TARGET: This replaces inline sessionStorage reads in the
 *   monolith. The community portal has a separate auth flow from staff.
 */

import { useContext } from 'react';
import { AppError } from '@cetused/shared';
import { AuthContext, type CommunityUser } from './AuthProvider';

export interface UseAuthResult {
  token: string | null;
  user: CommunityUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  loginWithPortalCode: (studentId: string, accessCode: string) => Promise<void>;
  loginWithMagicLink: (email: string) => Promise<void>;
  verifyMagicLink: (token: string) => Promise<void>;
  logout: () => void;
}

export function useAuth(): UseAuthResult {
  const context = useContext(AuthContext);
  if (!context) {
    throw AppError.internal('useAuth must be used within an AuthProvider. Wrap your component tree with <AuthProvider>.');
  }
  return context;
}
